// src/pages/Admin/Adherents/columns.config.jsx

import React from "react";
import { FaCopy } from "react-icons/fa";
import {
  formatDate,
  getGroupsText,
  getStatusDisplay,
  getUsersText,
  parseDateOnlyLocal,
} from "./helpers";

export const STORAGE_KEY = "monappli:dashboard:adherents:columns:v2";

export const DEFAULT_VISIBLE_COLUMN_IDS = [
  "status",
  "groups",
  "email",
  "birthdate",
  "phone",
  "commune",
];

export const ALL_COLUMNS = [
  {
    id: "status",
    label: "Statut",
    minWidth: 170,
    renderHeader: () => "Statut",
    renderCell: (adherent, ctx) => {
      const status = getStatusDisplay(adherent?.statut_inscription);
      const isEditing = String(ctx.editingStatusId) === String(adherent.id);

      if (isEditing) {
        return (
          <select
            value={adherent?.statut_inscription || ""}
            onChange={(event) => ctx.handleStatusChange(adherent.id, event.target.value)}
            onBlur={() => ctx.setEditingStatusId(null)}
            autoFocus
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            {ctx.statusOptions.map((statusOption) => (
              <option key={statusOption} value={statusOption}>
                {statusOption}
              </option>
            ))}
          </select>
        );
      }

      return (
        <button
          type="button"
          onClick={() => ctx.setEditingStatusId(adherent.id)}
          className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${status.className}`}
          title="Cliquer pour modifier le statut"
        >
          {status.label}
        </button>
      );
    },
  },
  {
    id: "groups",
    label: "Groupes",
    minWidth: 260,
    renderHeader: () => "Groupes",
    renderCell: (adherent, ctx) => {
      const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

      if (!groups.length) {
        return (
          <button
            type="button"
            onClick={() => ctx.openGroupsDrawer(adherent)}
            className="inline-flex rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 ring-1 ring-rose-200 transition hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
            title="Gérer les groupes"
          >
            Sans groupe
          </button>
        );
      }

      return (
        <button
          type="button"
          onClick={() => ctx.openGroupsDrawer(adherent)}
          className="flex max-w-[260px] flex-wrap gap-1 text-left"
          title="Gérer les groupes"
        >
          {groups.slice(0, 3).map((group) => (
            <span
              key={group.id || group.nom}
              className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${ctx.getGroupPillClasses(
                group
              )}`}
            >
              {group.nom}
            </span>
          ))}

          {groups.length > 3 ? (
            <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              +{groups.length - 3}
            </span>
          ) : null}
        </button>
      );
    },
  },
  {
    id: "email",
    label: "Email",
    minWidth: 300,
    renderHeader: (_rows, ctx) => (
      <div className="flex items-center gap-2">
        <span>Contact</span>

        <button
          type="button"
          onClick={ctx.copyFilteredEmails}
          className="inline-flex h-7 w-7 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
          title="Copier les emails filtrés"
        >
          <FaCopy />
        </button>
      </div>
    ),
    renderCell: (adherent) => (
      <div className="min-w-0">
        <div className="truncate text-slate-700 dark:text-slate-300">
          {adherent?.email || "—"}
        </div>

        <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
          {adherent?.email_secondaire || getUsersText(adherent) || "—"}
        </div>
      </div>
    ),
  },
  {
    id: "birthdate",
    label: "Naissance",
    minWidth: 150,
    renderHeader: () => "Naissance",
    renderCell: (adherent) => {
      const parsed = parseDateOnlyLocal(adherent?.date_naissance);

      return (
        <div className="text-slate-700 dark:text-slate-300">
          {parsed ? parsed.toLocaleDateString("fr-FR") : "—"}
        </div>
      );
    },
  },
  {
    id: "age",
    label: "Âge",
    minWidth: 100,
    renderHeader: () => "Âge",
    renderCell: (adherent, ctx) => (
      <div className="text-slate-700 dark:text-slate-300">
        {ctx.getAge(adherent?.date_naissance)}
      </div>
    ),
  },
  {
    id: "phone",
    label: "Téléphone",
    minWidth: 180,
    renderHeader: () => "Téléphone",
    renderCell: (adherent) => (
      <div className="text-slate-700 dark:text-slate-300">
        <div>{adherent?.tel || "—"}</div>
        {adherent?.tel_secondaire ? (
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {adherent.tel_secondaire}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    id: "commune",
    label: "Commune",
    minWidth: 180,
    renderHeader: () => "Commune",
    renderCell: (adherent) => (
      <div className="text-slate-700 dark:text-slate-300">
        <div className="truncate">{adherent?.commune || "—"}</div>
        {adherent?.cp ? (
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {adherent.cp}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    id: "licence",
    label: "Licence",
    minWidth: 180,
    renderHeader: () => "Licence",
    renderCell: (adherent) => (
      <div className="truncate text-slate-700 dark:text-slate-300">
        {adherent?.licence_number || "—"}
      </div>
    ),
  },
  {
    id: "genre",
    label: "Genre",
    minWidth: 120,
    renderHeader: () => "Genre",
    renderCell: (adherent) => (
      <div className="text-slate-700 dark:text-slate-300">
        {adherent?.genre || "—"}
      </div>
    ),
  },
  {
    id: "representant",
    label: "Représentant légal",
    minWidth: 240,
    renderHeader: () => "Représentant légal",
    renderCell: (adherent) => (
      <div className="truncate text-slate-700 dark:text-slate-300">
        {adherent?.representant_legal || "—"}
      </div>
    ),
  },
  {
    id: "referents",
    label: "Référents",
    minWidth: 260,
    renderHeader: () => "Référents",
    renderCell: (adherent) => (
      <div className="truncate text-slate-700 dark:text-slate-300">
        {getUsersText(adherent) || "—"}
      </div>
    ),
  },
  {
    id: "createdAt",
    label: "Créé le",
    minWidth: 150,
    renderHeader: () => "Créé le",
    renderCell: (adherent) => (
      <div className="text-slate-700 dark:text-slate-300">
        {formatDate(adherent?.createdAt || adherent?.created_at)}
      </div>
    ),
  },
  {
    id: "notes",
    label: "Notes",
    minWidth: 260,
    renderHeader: () => "Notes",
    renderCell: (adherent) => (
      <div className="max-w-[260px] truncate text-slate-700 dark:text-slate-300">
        {adherent?.notes_adherent || "—"}
      </div>
    ),
  },
];